import React from "react";
import { Button, Grid, TextField, Typography } from "@material-ui/core";
import styled from "styled-components";
import { withRouter, RouteComponentProps, Link } from "react-router-dom";
import WithLoadingSpinner from "../hoc/WithLoadingSpinner";
import Services from "../../services/Services";
import Theme from "../../Theme";
import { RawLinkButton } from "../LinkButton";
import IAPICredentials, { AuthOptions, IAPIResponse } from "../../models/IAPICredentials";
import { H3 } from "../Headers";
import FormSelect from "../forms/FormSelect";
import ISelectItem from "../../models/ISelectItem";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import { connect } from "react-redux";

const FormGrid = styled(Grid)`
  &&& .MuiFormLabel-asterisk {
    color: ${Theme.colors.error};
  }

  &&& .MuiGrid-spacing-xs-3 > .MuiGrid-item {
    width: 100%;
  }
`;

const GridColumn = styled(Grid)`
  width: 100%;
  margin: 0px;
`;

interface APICredentialsProps extends RouteComponentProps {
  isAuthenticated: boolean;
  token: string;
}

const ErrorLabel = styled(Typography)`
  color: red;
`;

const TextInput = styled(TextField)`
  width: 100%;
`;

const CategorySelect = styled(FormSelect)`
  width: 100%;
`;

const CopyButton = styled(Button)`
  margin: 10px;
  padding: 5px;
  width: 100%;
  background-color: lightgray;
`;

function getErrors(errors: string[], isAuthenticated: boolean, url: string) {
  return (
    <Grid item container direction="row" alignItems="flex-start" spacing={3}>
      {errors.map(err => (
        <Grid item key={err}>
          <ErrorLabel variant="subtitle2">{err}</ErrorLabel>
        </Grid>
      ))}
      {errors.length > 0 && isAuthenticated && (
        <Grid item>
          <Typography>
            Need help debugging your issue?
            <br />
            <Link
              to={{
                pathname: "/submit-sc",
                state: {
                  prefilledDescription: `API Base URL: "${url}"\n\n${errors.join("\n\n")}`,
                },
              }}
            >
              Submit it in a Support Call
            </Link>
            .
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

const SubmitAPICredentialsForm = (props: APICredentialsProps) => {
  const { isAuthenticated, token } = props;

  const [apiBaseURL, setAPIBaseURL] = React.useState("");
  const [key, setKey] = React.useState("");
  const [secret, setSecret] = React.useState("");
  const [authType, setAuthType] = React.useState("");
  const [errors, setErrors] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleAPIBaseURL = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAPIBaseURL(event.target.value);
  };

  const handleKey = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKey(event.target.value);
  };

  const handleSecret = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSecret(event.target.value);
  };

  const handleAuthType = (value: ISelectItem | null) => {
    if (value) {
      setAuthType(value.Name);
    } else {
      setAuthType("");
    }
  };

  const services: Services = new Services();

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    let apiCredsRepo = await services.APICredentialsRepository();
    let apiCredentials: IAPICredentials = {
      APIBaseURL: apiBaseURL,
      Key: key,
      Secret: secret,
      AuthType: authType,
    };

    try {
      setIsLoading(true);
      let apiResponse: IAPIResponse = await apiCredsRepo.SubmitAPICredentials(token, apiCredentials);
      setIsLoading(false);

      if (apiResponse && apiResponse.Errors && apiResponse.Errors.length) {
        apiResponse.Errors.unshift("Status Code " + apiResponse.StatusCode);
        throw apiResponse;
      } else if (apiResponse) {
        props.history.push("/test-credentials/success", apiResponse);
      }
    } catch (err) {
      setIsLoading(false);
      if (err["Errors"]) {
        setErrors(err["Errors"]);
      } else {
        setErrors([err.ToString()]);
      }
    }
  };

  return (
    <WithLoadingSpinner message="Submitting Credentials" isLoading={isLoading}>
      <FormGrid container direction="row" alignItems="flex-start" spacing={3}>
        <GridColumn item container xs={12}>
          <H3 center={true}>Enter your SMS credentials</H3>
        </GridColumn>
        <GridColumn item container direction="column" alignItems="center" spacing={3} md={errors.length > 0 ? 6 : 12} xs={12}>
          <Grid item>
            <TextInput id="apibaseurl" label="API Base URL" variant="outlined" required onChange={handleAPIBaseURL} value={apiBaseURL} />
          </Grid>
          <Grid item>
            <TextInput id="key" label="Key" variant="outlined" required onChange={handleKey} value={key} />
          </Grid>
          <Grid item>
            <TextInput id="secret" label="Secret" variant="outlined" required onChange={handleSecret} value={secret} />
          </Grid>
          <Grid item>
            <CategorySelect
              name="authtype"
              label="Authentication Type"
              required
              onValueChange={handleAuthType}
              options={AuthOptions}
              option={{ Name: authType }}
              prompt="Authentication"
            />
          </Grid>
        </GridColumn>
        {errors.length > 0 && (
          <GridColumn item container direction="column" alignItems="center" spacing={3} md={6} xs={12}>
            {getErrors(errors, isAuthenticated, apiBaseURL)}
          </GridColumn>
        )}
        <Grid item xs={12}>
          <RawLinkButton first onClick={handleSubmit} fullWidth={true}>
            Submit SMS Request
          </RawLinkButton>
        </Grid>
      </FormGrid>
    </WithLoadingSpinner>
  );
};

export default connect((state: IApplicationState) => ({
  ...selectAuth(state),
}))(withRouter(SubmitAPICredentialsForm));
